import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Handshake,
  Search,
  Phone,
  MapPin,
  ArrowRight,
  Camera,
  FileText,
  Users,
  ShieldCheck,
  Wallet,
  Wrench,
  Network,
  Car,
  Fuel,
  Cog,
  Calendar,
  Gauge,
  X,
  ChevronLeft,
  ChevronRight,
  Check,
  Sparkles,
} from "lucide-react";
import { Toaster } from "sonner";
import { useSiteMeta } from "./hooks/useSiteMeta";
import { useContactInfo } from "./hooks/useContactInfo";
import { Navbar, Footer } from "./App";
import { buildImageUrl } from "./components/depotvente/DepotVenteListingForm";
import { AnimationEmbed } from "./components/AnimationEmbed";

const API = process.env.REACT_APP_BACKEND_URL;

const AchatVentePage = () => {
  const [businessHours, setBusinessHours] = useState([]);
  const [homeContent, setHomeContent] = useState({});
  const [reviews, setReviews] = useState({ rating: 4.8, user_ratings_total: 107, url: "" });
  const [depotContent, setDepotContent] = useState({ listings: [] });
  const [cms, setCms] = useState({});
  const contact = useContactInfo();
  const [detailListing, setDetailListing] = useState(null);

  const cmsSeo = cms?.seo || {};
  useSiteMeta({
    title: cmsSeo.title || "Dépôt-Vente Voiture Clermont-Ferrand | ATR Racing",
    description:
      cmsSeo.description ||
      "ATR gère la vente de votre voiture ou trouve le véhicule qu'il vous faut à Clermont-Ferrand. Estimation gratuite, annonces, visites et paperasse — on s'occupe de tout.",
    canonical_path: "/achat-vente-voiture-clermont-ferrand",
    og_image: `${window.location.origin}/og/achat-vente.jpg`,
    og_image_alt: "Dépôt-vente voiture — Auvergne Tech Racing, Clermont-Ferrand",
  });

  // Filters (public page)
  const [filterBrand, setFilterBrand] = useState("");
  const [filterFuel, setFilterFuel] = useState("");
  const [filterGearbox, setFilterGearbox] = useState("");
  const [filterMaxPrice, setFilterMaxPrice] = useState("");
  const [filterStatus, setFilterStatus] = useState(""); // "" = all shown

  useEffect(() => {
    axios.get(`${API}/api/business-hours`).then((r) => setBusinessHours(r.data)).catch(() => {});
    axios.get(`${API}/api/content/home_landing`).then((r) => setHomeContent(r.data)).catch(() => {});
    axios.get(`${API}/api/content/achat_vente_page`).then((r) => setCms(r.data || {})).catch(() => {});
    axios
      .get(`${API}/api/content/depot_vente_listings`)
      .then((r) => setDepotContent(r.data || { listings: [] }))
      .catch(() => {});
    axios
      .get(`${API}/api/google-reviews`)
      .then((r) =>
        setReviews({
          rating: r.data.rating ?? 4.8,
          user_ratings_total: r.data.user_ratings_total ?? 107,
          url: r.data.url || "",
        })
      )
      .catch(() => {});
  }, []);

  const hero = cms.hero || {};
  const sellSection = cms.sell_section || {};
  const searchSection = cms.search_section || {};
  const finalCta = cms.final_cta || {};

  const sellStepIcons = [Wallet, Camera, Users, FileText];
  const searchStepIcons = [Wallet, Search, ShieldCheck, FileText];
  const advantageIcons = [Wrench, Network, Handshake, ShieldCheck, Wallet];

  const cmsSellSteps = Array.isArray(sellSection.steps) && sellSection.steps.length ? sellSection.steps : null;
  const sellSteps = (cmsSellSteps || [
    { title: "Estimation gratuite", text: "Estimation gratuite et prix de vente fixé avec vous." },
    { title: "Photos & annonces", text: "Préparation du véhicule, photos professionnelles et diffusion des annonces." },
    { title: "Visites & essais", text: "On gère les appels, les visites et les essais à votre place." },
    { title: "Papiers & paiement", text: "Papiers, carte grise et paiement sécurisé." },
  ]).map((s, i) => ({ ...s, icon: sellStepIcons[i] || Wallet }));

  const cmsSearchSteps = Array.isArray(searchSection.steps) && searchSection.steps.length ? searchSection.steps : null;
  const searchSteps = (cmsSearchSteps || [
    { title: "Budget & critères", text: "On définit ensemble votre budget et vos critères précis." },
    { title: "Sélection annonces", text: "On sélectionne uniquement les annonces sérieuses." },
    { title: "Vérification technique", text: "Vérification technique et historique du véhicule avant achat." },
    { title: "Négo & livraison", text: "Négociation, transport et livraison prête à rouler." },
  ]).map((s, i) => ({ ...s, icon: searchStepIcons[i] || Wallet }));

  const cmsAdvantages = Array.isArray(cms.advantages) && cms.advantages.length ? cms.advantages : null;
  const advantages = (cmsAdvantages || [
    "Expertise mécanique : on voit les défauts cachés qu'un particulier ne voit pas",
    "Réseau local à Clermont-Ferrand et Auvergne",
    "Zéro prise de tête : annonces, appels, essais — on gère tout",
    "Paiement sécurisé et démarches administratives comprises",
    "Estimation honnête et transparente",
  ]).map((a, i) => ({
    icon: advantageIcons[i] || Wrench,
    text: typeof a === "string" ? a : a.text,
  }));

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "AutoDealer",
    name: `${contact.business_name} — Dépôt-vente & Achat véhicule`,
    description:
      "Service de dépôt-vente et de recherche de véhicule à Clermont-Ferrand. Estimation gratuite, annonces, visites, vérification technique et démarches administratives.",
    telephone: contact.phone,
    address: {
      "@type": "PostalAddress",
      streetAddress: contact.street,
      addressLocality: contact.city,
      postalCode: contact.postal_code,
      addressCountry: contact.country,
    },
    areaServed: [
      { "@type": "City", name: contact.city },
      { "@type": "AdministrativeArea", name: "Auvergne" },
    ],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: reviews.rating || 4.8,
      reviewCount: reviews.user_ratings_total || 107,
      bestRating: 5,
      worstRating: 1,
    },
  };

  return (
    <div className="bg-[#0A0A0A] min-h-screen" data-testid="achat-vente-page">
      <script
        type="application/ld+json"
        data-testid="achat-vente-jsonld"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Toaster position="top-right" richColors />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden">
        {cms.hero_bg_image_url && (
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url('${cms.hero_bg_image_url}')` }}
            data-testid="achat-vente-hero-bg"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#0A0A0A]" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF3B30]/[0.08] via-transparent to-transparent" />
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-full mb-6">
            <Handshake size={14} className="text-[#FF3B30]" />
            <span className="text-[#FF3B30] text-xs font-bold uppercase tracking-wider">
              {hero.badge || "Dépôt-Vente · Recherche Véhicule"}
            </span>
          </div>

          <h1
            className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight text-white mb-6 leading-tight"
            data-testid="achat-vente-h1"
          >
            {cmsSeo.h1_prefix || hero.h1_top || "Achat & Dépôt-Vente de Voiture"}
            <br />
            <span className="text-[#FF3B30]">{cmsSeo.h1_highlight || hero.h1_highlight || "Clermont-Ferrand"}</span>
          </h1>

          <p className="text-lg md:text-xl text-white/70 leading-relaxed mb-8 max-w-3xl">
            {hero.intro || "Vous voulez vendre votre voiture sans la galère des annonces, des appels et des curieux ? Ou vous cherchez le bon véhicule mais vous n'avez pas le temps de trier les arnaques ? Auvergne Tech Racing propose deux services clés en main à Clermont-Ferrand : le dépôt-vente et la recherche de véhicule."}{" "}
            <span className="text-white font-semibold">{hero.intro_bold || "Vous déposez, on gère."}</span>
          </p>

          <div className="flex flex-wrap gap-3 items-center">
            <a
              href={`tel:${contact.phone}`}
              data-testid="achat-vente-cta-call"
              className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D63025] text-white font-bold px-6 py-4 rounded-lg transition-colors uppercase tracking-wide text-sm"
            >
              <Phone size={16} />
              {hero.cta_call_label || "Estimation gratuite — 06 49 07 08 20"}
            </a>
          </div>
        </div>
      </section>

      {/* Animation Dépôt-Vente */}
      <section className="py-12 md:py-16 bg-[#0A0A0A] border-t border-white/[0.06]" data-testid="achat-vente-page-animation-section">
        <div className="max-w-5xl mx-auto px-6">
          <AnimationEmbed src="/depot-vente.html" title="Dépôt-Vente Voiture" testId="achat-vente-animation" />
        </div>
      </section>

      {/* Dépôt-Vente */}
      <section className="py-16 md:py-24 bg-[#121212] border-t border-white/[0.06]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-full mb-4">
            <span className="text-[#FF3B30] text-xs font-bold uppercase tracking-wider">{sellSection.badge || "Service 1 — Vendre"}</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">
            {sellSection.title_top || "Dépôt-Vente — On vend votre voiture à"} <span className="text-[#FF3B30]">{sellSection.title_highlight || "votre place"}</span>
          </h2>
          <p className="text-white/70 leading-relaxed max-w-3xl mb-12">
            {sellSection.description || "Vous confiez votre véhicule à notre atelier, on s'occupe de tout jusqu'à la vente finale. Prix fixé avec vous, annonces diffusées, visites gérées, papiers bouclés. Vous touchez votre argent, sans stress."}
          </p>

          <h3 className="text-xl font-bold text-white/80 uppercase tracking-wider mb-6">{sellSection.steps_title || "Comment ça marche"}</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {sellSteps.map((s, i) => {
              const Icon = s.icon;
              return (
                <article
                  key={i}
                  data-testid={`achat-vente-sell-${i}`}
                  className="bg-black/40 border border-white/[0.06] rounded-xl p-6 flex gap-4 items-start hover:border-[#FF3B30]/30 transition-colors"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#FF3B30]/[0.12] flex items-center justify-center relative">
                    <Icon size={18} className="text-[#FF3B30]" />
                    <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#FF3B30] text-white text-xs font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">{s.title}</h4>
                    <p className="text-white/60 text-sm leading-relaxed">{s.text}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Véhicules du moment (vitrine dépôt-vente) */}
      <section
        id="vehicules-du-moment"
        className="py-16 md:py-24 bg-[#0A0A0A] border-t border-white/[0.06]"
        data-testid="depot-vitrine"
      >
        <div className="max-w-6xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-full mb-4">
            <Car size={14} className="text-[#FF3B30]" />
            <span className="text-[#FF3B30] text-xs font-bold uppercase tracking-wider">En dépôt actuellement</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 tracking-tight">
            {depotContent.section_title || "Véhicules du moment"}
          </h2>
          <p className="text-white/60 max-w-2xl mb-8">
            {depotContent.section_subtitle || "Sélection actuelle en dépôt-vente — mise à jour régulièrement."}
          </p>

          {/* Filters */}
          <ListingsFilters
            listings={depotContent.listings || []}
            filterBrand={filterBrand}
            setFilterBrand={setFilterBrand}
            filterFuel={filterFuel}
            setFilterFuel={setFilterFuel}
            filterGearbox={filterGearbox}
            setFilterGearbox={setFilterGearbox}
            filterMaxPrice={filterMaxPrice}
            setFilterMaxPrice={setFilterMaxPrice}
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
          />

          {(() => {
            const allListings = depotContent.listings || [];
            const filtered = applyListingFilters(allListings, {
              brand: filterBrand,
              fuel: filterFuel,
              gearbox: filterGearbox,
              maxPrice: filterMaxPrice,
              status: filterStatus,
            });
            if (allListings.length === 0) {
              return (
                <div data-testid="depot-empty" className="text-center py-16 bg-black/40 border border-white/[0.06] rounded-xl">
                  <Car size={40} className="text-white/20 mx-auto mb-4" />
                  <p className="text-white/60 max-w-md mx-auto">
                    {depotContent.empty_state || "Nouveaux véhicules très bientôt — contactez-nous pour être alerté en premier."}
                  </p>
                </div>
              );
            }
            if (filtered.length === 0) {
              return (
                <div data-testid="depot-empty-filtered" className="text-center py-12 bg-black/40 border border-white/[0.06] rounded-xl">
                  <Search size={36} className="text-white/20 mx-auto mb-3" />
                  <p className="text-white/60 mb-3">Aucun véhicule ne correspond à ces critères.</p>
                  <button
                    onClick={() => { setFilterBrand(""); setFilterFuel(""); setFilterGearbox(""); setFilterMaxPrice(""); setFilterStatus(""); }}
                    className="text-[#FF3B30] hover:text-[#ff6b62] text-sm font-semibold uppercase tracking-wider"
                  >
                    Réinitialiser les filtres
                  </button>
                </div>
              );
            }
            return (
              <>
                <div className="text-white/50 text-xs mb-4" data-testid="depot-results-count">
                  {filtered.length} véhicule{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""}{filtered.length !== allListings.length ? ` sur ${allListings.length}` : ""}
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filtered.map((v, i) => {
                    const sold = v.status === "vendu";
                    const reserved = v.status === "reserve";
                    const cover = v.image_url || (Array.isArray(v.images) && v.images[0]) || "";
                    const photosCount = (v.images?.length || 0) + (v.image_url ? 1 : 0);
                    return (
                      <article
                        key={v.id || i}
                        data-testid={`depot-listing-${i}`}
                        className="bg-black/40 border border-white/[0.06] rounded-xl overflow-hidden hover:border-[#FF3B30]/30 transition-colors flex flex-col cursor-pointer group"
                        onClick={() => !sold && setDetailListing(v)}
                  >
                    {/* Image / placeholder */}
                    <div className="relative aspect-[16/10] bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] overflow-hidden">
                      {cover ? (
                        <img
                          src={buildImageUrl(cover)}
                          alt={v.title}
                          loading="lazy"
                          className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${sold ? "grayscale opacity-60" : ""}`}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Car size={64} className="text-white/10" />
                        </div>
                      )}

                      {/* Photo count badge */}
                      {photosCount > 1 && (
                        <span className="absolute bottom-3 left-3 bg-black/70 text-white text-[10px] font-bold px-2 py-1 rounded flex items-center gap-1">
                          <Camera size={11} /> {photosCount} photos
                        </span>
                      )}

                      {/* Status badge */}
                      {sold && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="rotate-[-12deg] bg-white/95 text-black text-2xl font-black tracking-widest px-6 py-2 border-4 border-black">
                            VENDU
                          </span>
                        </div>
                      )}
                      {reserved && (
                        <span className="absolute top-3 right-3 bg-yellow-500/90 text-black text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded">
                          Réservé
                        </span>
                      )}
                      {!sold && !reserved && (
                        <span className="absolute top-3 right-3 bg-green-500/90 text-white text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded">
                          Disponible
                        </span>
                      )}
                    </div>

                    {/* Body */}
                    <div className="p-5 flex-1 flex flex-col">
                      <h3 className="text-white font-bold text-lg mb-3 leading-tight">{v.title}</h3>

                      {/* Specs row */}
                      <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm text-white/70 mb-4">
                        {v.year && (
                          <div className="flex items-center gap-1.5">
                            <Calendar size={13} className="text-[#FF3B30]" />
                            <span>{v.year}</span>
                          </div>
                        )}
                        {v.km && (
                          <div className="flex items-center gap-1.5">
                            <Gauge size={13} className="text-[#FF3B30]" />
                            <span>{v.km}</span>
                          </div>
                        )}
                        {v.fuel && (
                          <div className="flex items-center gap-1.5">
                            <Fuel size={13} className="text-[#FF3B30]" />
                            <span>{v.fuel}</span>
                          </div>
                        )}
                        {v.gearbox && (
                          <div className="flex items-center gap-1.5">
                            <Cog size={13} className="text-[#FF3B30]" />
                            <span>{v.gearbox}</span>
                          </div>
                        )}
                      </div>

                      {/* Engine badge */}
                      {(v.engine || v.power_din) && (
                        <div className="text-white/50 text-xs mb-3">
                          {[v.engine, v.power_din && `${v.power_din} ch`].filter(Boolean).join(" · ")}
                        </div>
                      )}

                      {/* Highlights */}
                      {v.highlights && v.highlights.length > 0 && (
                        <ul className="mb-4 space-y-1">
                          {v.highlights.slice(0, 3).map((h, j) => (
                            <li key={j} className="text-white/60 text-xs flex items-start gap-1.5">
                              <span className="text-[#FF3B30] mt-0.5">•</span>
                              <span>{h}</span>
                            </li>
                          ))}
                        </ul>
                      )}

                      {/* Price + CTA */}
                      <div className="mt-auto pt-4 border-t border-white/[0.06] flex items-center justify-between gap-3">
                        {v.price && (
                          <span className={`font-bold text-xl ${sold ? "text-white/50 line-through" : "text-[#FF3B30]"}`}>
                            {v.price}
                          </span>
                        )}
                        {!sold && (
                          <span
                            data-testid={`depot-listing-cta-${i}`}
                            className="inline-flex items-center gap-1.5 bg-[#FF3B30] hover:bg-[#D63025] text-white font-bold px-4 py-2 rounded-md transition-colors text-xs uppercase tracking-wide"
                          >
                            Voir la fiche <ArrowRight size={12} />
                          </span>
                        )}
                      </div>
                    </div>
                  </article>
                );
                  })}
                </div>
              </>
            );
          })()}
        </div>
      </section>

      {/* Vehicle detail modal */}
      {detailListing && (
        <VehicleDetailModal
          v={detailListing}
          onClose={() => setDetailListing(null)}
          contactPhone={depotContent.cta_phone || contact.phone}
          contactPhoneDisplay={depotContent.cta_phone_display || contact.phone_display}
          contactLabel={depotContent.cta_label || "Nous contacter"}
        />
      )}

      {/* Recherche véhicule */}
      <section className="py-16 md:py-24 bg-[#0A0A0A] border-t border-white/[0.06]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-full mb-4">
            <span className="text-[#FF3B30] text-xs font-bold uppercase tracking-wider">{searchSection.badge || "Service 2 — Acheter"}</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">
            {searchSection.title_top || "Recherche de Véhicule — On trouve la voiture"} <span className="text-[#FF3B30]">{searchSection.title_highlight || "qu'il vous faut"}</span>
          </h2>
          <p className="text-white/70 leading-relaxed max-w-3xl mb-12">
            {searchSection.description || "Vous avez un budget et des critères — modèle, kilométrage, boîte, motorisation. On sélectionne uniquement les annonces sérieuses, on vérifie techniquement le véhicule avant achat et on s'occupe de la négociation et du transport jusqu'à l'atelier."}
          </p>

          <h3 className="text-xl font-bold text-white/80 uppercase tracking-wider mb-6">{searchSection.steps_title || "Notre process d'achat accompagné"}</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {searchSteps.map((s, i) => {
              const Icon = s.icon;
              return (
                <article
                  key={i}
                  data-testid={`achat-vente-search-${i}`}
                  className="bg-black/40 border border-white/[0.06] rounded-xl p-6 flex gap-4 items-start hover:border-[#FF3B30]/30 transition-colors"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#FF3B30]/[0.12] flex items-center justify-center relative">
                    <Icon size={18} className="text-[#FF3B30]" />
                    <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#FF3B30] text-white text-xs font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">{s.title}</h4>
                    <p className="text-white/60 text-sm leading-relaxed">{s.text}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Avantages */}
      <section className="py-16 md:py-24 bg-[#121212] border-t border-white/[0.06]">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-10 tracking-tight text-center">
            {cms.advantages_title_before || "Pourquoi confier votre véhicule à"} <span className="text-[#FF3B30]">{cms.advantages_title_highlight || "ATR"}</span>{cms.advantages_title_after || " ?"}
          </h2>

          <ul className="space-y-4">
            {advantages.map((a, i) => {
              const Icon = a.icon;
              return (
                <li
                  key={i}
                  data-testid={`achat-vente-advantage-${i}`}
                  className="flex items-start gap-4 bg-black/40 border border-white/[0.06] rounded-lg p-4"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#FF3B30]/[0.12] flex items-center justify-center">
                    <Icon size={18} className="text-[#FF3B30]" />
                  </div>
                  <span className="text-white/80 leading-relaxed pt-2">{a.text}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* CTA Estimation */}
      <section className="py-16 md:py-24 bg-[#0A0A0A] border-t border-white/[0.06]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">
            {finalCta.title_before || "Demandez votre"} <span className="text-[#FF3B30]">{finalCta.title_highlight || "estimation gratuite"}</span>
          </h2>
          <p className="text-white/70 mb-10 max-w-2xl mx-auto">
            {finalCta.subtitle || "Contactez-nous directement pour une estimation gratuite de votre véhicule ou pour nous décrire la voiture que vous recherchez."}
          </p>

          <div className="flex flex-wrap gap-4 justify-center mb-10">
            <a
              href={`tel:${contact.phone}`}
              data-testid="achat-vente-cta-phone"
              className="inline-flex items-center gap-3 bg-[#FF3B30] hover:bg-[#D63025] text-white font-bold px-8 py-4 rounded-lg transition-colors uppercase tracking-wide text-sm"
            >
              <Phone size={18} />
              {finalCta.button_label || "Nous contacter — 06 49 07 08 20"}
              <ArrowRight size={16} />
            </a>
          </div>

          <a
            href={reviews.url || "https://maps.google.com/?cid=1681708394347082412"}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="achat-vente-cta-maps"
            className="inline-flex items-center gap-3 bg-black/40 border border-white/[0.06] hover:border-[#FF3B30]/30 rounded-xl p-5 transition-colors text-left"
          >
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#FF3B30]/[0.12] flex items-center justify-center">
              <MapPin size={20} className="text-[#FF3B30]" />
            </div>
            <div>
              <div className="text-white font-bold">Auvergne Tech Racing</div>
              <div className="text-white/60 text-sm">{contact.fullAddress}</div>
            </div>
          </a>
        </div>
      </section>

      <Footer businessHours={businessHours} content={homeContent} />
    </div>
  );
};

// ============== VEHICLE DETAIL MODAL ==============
const VehicleDetailModal = ({ v, onClose, contactPhone, contactPhoneDisplay, contactLabel }) => {
  const allImages = [v.image_url, ...(Array.isArray(v.images) ? v.images : [])].filter(Boolean);
  const [idx, setIdx] = useState(0);
  const sold = v.status === "vendu";
  const reserved = v.status === "reserve";

  // Close on Escape
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowRight") setIdx((i) => Math.min(i + 1, allImages.length - 1)); if (e.key === "ArrowLeft") setIdx((i) => Math.max(i - 1, 0)); };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", h);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", h); };
  }, [allImages.length, onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm overflow-y-auto overscroll-contain"
      onClick={onClose}
      data-testid="vehicle-detail-modal"
    >
      <div className="min-h-full flex items-start sm:items-center justify-center p-4">
        <div
          className="relative bg-[#121212] border border-white/[0.08] rounded-xl w-full max-w-5xl my-8 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
        {/* Close */}
        <button
          onClick={onClose}
          data-testid="vehicle-detail-close"
          className="absolute top-3 right-3 z-20 bg-black/70 hover:bg-black/90 text-white rounded-full p-2 transition-colors"
          aria-label="Fermer"
        >
          <X size={18} />
        </button>

        {/* Gallery */}
        <div className="relative aspect-[16/9] bg-black">
          {allImages.length > 0 ? (
            <>
              <img
                key={idx}
                src={buildImageUrl(allImages[idx])}
                alt={v.title}
                className={`w-full h-full object-contain ${sold ? "grayscale opacity-60" : ""}`}
              />
              {allImages.length > 1 && (
                <>
                  <button
                    onClick={() => setIdx((i) => Math.max(0, i - 1))}
                    disabled={idx === 0}
                    data-testid="vehicle-detail-prev"
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/70 hover:bg-black text-white p-2 rounded-full disabled:opacity-30"
                    aria-label="Photo précédente"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    onClick={() => setIdx((i) => Math.min(allImages.length - 1, i + 1))}
                    disabled={idx === allImages.length - 1}
                    data-testid="vehicle-detail-next"
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/70 hover:bg-black text-white p-2 rounded-full disabled:opacity-30"
                    aria-label="Photo suivante"
                  >
                    <ChevronRight size={20} />
                  </button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white text-xs font-bold px-3 py-1 rounded-full">
                    {idx + 1} / {allImages.length}
                  </div>
                </>
              )}
              {sold && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <span className="rotate-[-12deg] bg-white/95 text-black text-4xl font-black tracking-widest px-8 py-3 border-4 border-black">
                    VENDU
                  </span>
                </div>
              )}
              {reserved && !sold && (
                <span className="absolute top-6 left-6 bg-yellow-500/90 text-black text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded">
                  Réservé
                </span>
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Car size={80} className="text-white/10" />
            </div>
          )}
        </div>

        {/* Thumbnails */}
        {allImages.length > 1 && (
          <div className="flex gap-2 p-4 overflow-x-auto bg-black/40 border-b border-white/[0.06]">
            {allImages.map((url, i) => (
              <button
                key={`${url}-${i}`}
                onClick={() => setIdx(i)}
                className={`flex-shrink-0 w-20 h-14 rounded overflow-hidden border-2 transition-colors ${idx === i ? "border-[#FF3B30]" : "border-transparent hover:border-white/20"}`}
              >
                <img src={buildImageUrl(url)} alt={`Miniature ${i + 1}`} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="p-6 md:p-8 space-y-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <h2 className="text-white text-2xl md:text-3xl font-bold mb-2" data-testid="vehicle-detail-title">{v.title}</h2>
              <div className="flex flex-wrap gap-2 text-white/70 text-sm">
                {v.year && <SpecPill icon={<Calendar size={13} />}>{v.year}</SpecPill>}
                {v.km && <SpecPill icon={<Gauge size={13} />}>{v.km}</SpecPill>}
                {v.fuel && <SpecPill icon={<Fuel size={13} />}>{v.fuel}</SpecPill>}
                {v.gearbox && <SpecPill icon={<Cog size={13} />}>{v.gearbox}</SpecPill>}
                {v.power_din && <SpecPill icon={<Sparkles size={13} />}>{v.power_din} ch</SpecPill>}
              </div>
            </div>
            {v.price && (
              <div className="text-right">
                <div className={`text-3xl font-bold ${sold ? "text-white/50 line-through" : "text-[#FF3B30]"}`}>{v.price}</div>
              </div>
            )}
          </div>

          {/* Description */}
          {v.description && (
            <div className="text-white/80 leading-relaxed whitespace-pre-wrap">{v.description}</div>
          )}

          {/* Detailed specs */}
          {(v.brand || v.model || v.trim || v.first_registration || v.engine || v.power_din || v.fiscal_hp || v.displacement || v.doors || v.seats || v.color_exterior || v.color_interior || v.co2 || v.crit_air || v.previous_owners || v.keys_count || v.service_book || v.ct_valid_until) && (
            <div data-testid="vehicle-detail-specs">
              <h3 className="text-white/70 text-xs uppercase tracking-widest font-bold mb-3">Caractéristiques</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                <SpecRow label="Marque" value={v.brand} />
                <SpecRow label="Modèle" value={v.model} />
                <SpecRow label="Finition" value={v.trim} />
                <SpecRow label="1ère immat" value={v.first_registration} />
                <SpecRow label="Motorisation" value={v.engine} />
                <SpecRow label="Puissance" value={v.power_din ? `${v.power_din} ch` : ""} />
                <SpecRow label="CV fiscaux" value={v.fiscal_hp} />
                <SpecRow label="Cylindrée" value={v.displacement ? `${v.displacement} cm³` : ""} />
                <SpecRow label="Portes" value={v.doors} />
                <SpecRow label="Places" value={v.seats} />
                <SpecRow label="Couleur ext." value={v.color_exterior} />
                <SpecRow label="Couleur int." value={v.color_interior} />
                <SpecRow label="CO₂" value={v.co2 ? `${v.co2} g/km` : ""} />
                <SpecRow label="Crit'Air" value={v.crit_air} />
                <SpecRow label="Propriétaires précédents" value={v.previous_owners} />
                <SpecRow label="Nombre de clés" value={v.keys_count} />
                <SpecRow label="Carnet d'entretien" value={v.service_book} />
                <SpecRow label="CT valide jusqu'au" value={v.ct_valid_until} />
              </div>
            </div>
          )}

          {/* Options */}
          {v.options && v.options.length > 0 && (
            <div>
              <h3 className="text-white/70 text-xs uppercase tracking-widest font-bold mb-3">Équipements ({v.options.length})</h3>
              <div className="flex flex-wrap gap-2">
                {v.options.map((o, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 text-sm text-white/80 bg-white/[0.06] border border-white/[0.06] px-3 py-1.5 rounded-full">
                    <Check size={13} className="text-[#FF3B30]" /> {o}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Highlights */}
          {v.highlights && v.highlights.length > 0 && (
            <div>
              <h3 className="text-white/70 text-xs uppercase tracking-widest font-bold mb-3">Points forts</h3>
              <ul className="space-y-1.5">
                {v.highlights.map((h, i) => (
                  <li key={i} className="text-white/80 text-sm flex items-start gap-2">
                    <span className="text-[#FF3B30] mt-1">•</span>
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Contact CTA */}
          {!sold && (
            <div className="pt-4 border-t border-white/[0.06] flex flex-col sm:flex-row gap-3">
              <a
                href={`tel:${contactPhone}`}
                data-testid="vehicle-detail-cta"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#FF3B30] hover:bg-[#D63025] text-white font-bold px-6 py-3 rounded-md transition-colors uppercase tracking-wide text-sm"
              >
                <Phone size={14} />
                {contactLabel} — {contactPhoneDisplay || contactPhone}
              </a>
              <a
                href={`https://wa.me/${(contactPhone || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Bonjour, je suis intéressé par le véhicule "${v.title}" — pouvez-vous me donner plus d'informations ?`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20BA5C] text-white font-bold px-6 py-3 rounded-md transition-colors uppercase tracking-wide text-sm"
              >
                WhatsApp
              </a>
              <a
                href={`/depot-vente/fiche/${v.id}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="vehicle-detail-pdf"
                className="inline-flex items-center justify-center gap-2 bg-white/[0.06] hover:bg-white/[0.12] text-white/90 font-bold px-6 py-3 rounded-md transition-colors uppercase tracking-wide text-sm"
              >
                Fiche PDF
              </a>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

const SpecPill = ({ icon, children }) => (
  <span className="inline-flex items-center gap-1.5 bg-white/[0.04] border border-white/[0.06] px-2.5 py-1 rounded-full">
    <span className="text-[#FF3B30]">{icon}</span> {children}
  </span>
);

const SpecRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <div>
      <div className="text-white/40 text-[10px] uppercase tracking-wider">{label}</div>
      <div className="text-white text-sm">{value}</div>
    </div>
  );
};

// ============== FILTERS ==============
// Extract a numeric price from strings like "12 900 €", "12,900€", "12900"
const parsePrice = (raw) => {
  if (!raw) return null;
  const digits = String(raw).replace(/[^\d]/g, "");
  const n = parseInt(digits, 10);
  return Number.isFinite(n) ? n : null;
};

export const applyListingFilters = (listings, filters) => {
  return listings.filter((v) => {
    if (filters.status && filters.status !== "all" && v.status !== filters.status) return false;
    if (filters.brand) {
      const listingBrand = (v.brand || v.title || "").toLowerCase();
      if (!listingBrand.includes(filters.brand.toLowerCase())) return false;
    }
    if (filters.fuel && v.fuel !== filters.fuel) return false;
    if (filters.gearbox && v.gearbox !== filters.gearbox) return false;
    if (filters.maxPrice) {
      const priceNum = parsePrice(v.price);
      const maxNum = parseInt(filters.maxPrice, 10);
      if (priceNum !== null && Number.isFinite(maxNum) && priceNum > maxNum) return false;
    }
    return true;
  });
};

const ListingsFilters = ({
  listings,
  filterBrand, setFilterBrand,
  filterFuel, setFilterFuel,
  filterGearbox, setFilterGearbox,
  filterMaxPrice, setFilterMaxPrice,
  filterStatus, setFilterStatus,
}) => {
  // Extract unique values from actual data for select options
  const brands = Array.from(new Set(
    listings.map((l) => l.brand || (l.title || "").split(" ")[0]).filter(Boolean)
  )).sort();
  const fuels = Array.from(new Set(listings.map((l) => l.fuel).filter(Boolean))).sort();
  const gearboxes = Array.from(new Set(listings.map((l) => l.gearbox).filter(Boolean))).sort();

  const hasFilters = filterBrand || filterFuel || filterGearbox || filterMaxPrice || filterStatus;

  const reset = () => {
    setFilterBrand(""); setFilterFuel(""); setFilterGearbox(""); setFilterMaxPrice(""); setFilterStatus("");
  };

  const selectCls = "w-full bg-black/40 border border-white/[0.08] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF3B30]";

  return (
    <div className="mb-6 bg-black/30 border border-white/[0.06] rounded-xl p-4" data-testid="depot-filters">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-white/70 text-xs uppercase tracking-widest font-bold">
          <Search size={14} className="text-[#FF3B30]" />
          Filtrer
        </div>
        {hasFilters && (
          <button
            onClick={reset}
            data-testid="depot-filters-reset"
            className="text-white/50 hover:text-white text-xs font-semibold uppercase tracking-wider"
          >
            Réinitialiser
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <select value={filterBrand} onChange={(e) => setFilterBrand(e.target.value)} data-testid="filter-brand" className={selectCls}>
          <option value="">Toutes marques</option>
          {brands.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <select value={filterFuel} onChange={(e) => setFilterFuel(e.target.value)} data-testid="filter-fuel" className={selectCls}>
          <option value="">Tous carburants</option>
          {fuels.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <select value={filterGearbox} onChange={(e) => setFilterGearbox(e.target.value)} data-testid="filter-gearbox" className={selectCls}>
          <option value="">Toutes boîtes</option>
          {gearboxes.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <select value={filterMaxPrice} onChange={(e) => setFilterMaxPrice(e.target.value)} data-testid="filter-price" className={selectCls}>
          <option value="">Budget max</option>
          <option value="5000">≤ 5 000 €</option>
          <option value="10000">≤ 10 000 €</option>
          <option value="15000">≤ 15 000 €</option>
          <option value="20000">≤ 20 000 €</option>
          <option value="30000">≤ 30 000 €</option>
          <option value="50000">≤ 50 000 €</option>
          <option value="100000">≤ 100 000 €</option>
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="filter-status" className={selectCls}>
          <option value="">Tous statuts</option>
          <option value="disponible">Disponible</option>
          <option value="reserve">Réservé</option>
          <option value="vendu">Vendu</option>
        </select>
      </div>
    </div>
  );
};

export default AchatVentePage;
